/**
 * The rail's contract — PRD 04 §6, "the shell and the rail refinement (decided)".
 *
 * One rail, three modes and a constant floor. The mode is a property of the *surface*, so it
 * is declared on the route (`handle.rail`) rather than pushed up from a component: the shell
 * knows what the rail is before the surface renders, which is why the rail never flashes the
 * wrong mode on a navigation.
 *
 * What a surface still supplies at runtime — its title, its rows, its local cost — comes
 * through `useRailContent`.
 */

/**
 * - `shelf` — kinds, counts, search. Library home, and Compose as the *source* you pull from.
 * - `siblings` — something is open; the rail lists its peers.
 * - `state` — nothing open and no shelf (Stories home): what is being made, what needs you.
 * - `none` — Browse only. The shared shelf, so none of the three apply, and the mode-shift
 *   itself signals "you've left your workspace".
 */
export type RailMode = 'shelf' | 'siblings' | 'state' | 'none';

/** `working` is the default half-sheet; `source` firms up to a full sheet on the composer. */
export type RailGround = 'working' | 'source';

export interface RailPreset {
  mode: RailMode;
  /**
   * **Recede is not hidden.** Dimmed to the floor, width constant so surfaces don't jump,
   * and never hand-collapsible. The meet-your-character moment and story intake use it.
   */
  recede?: boolean;
  ground?: RailGround;
}

/** What a route declares. Read off `useMatches()` by the shell. */
export interface RailHandle {
  rail: RailPreset;
}

export const RAIL_SHELF: RailPreset = { mode: 'shelf' };
export const RAIL_SIBLINGS: RailPreset = { mode: 'siblings' };
export const RAIL_STATE: RailPreset = { mode: 'state' };
export const RAIL_SOURCE: RailPreset = { mode: 'shelf', ground: 'source' };
export const RAIL_RECEDE: RailPreset = { mode: 'siblings', recede: true };
export const RAIL_NONE: RailPreset = { mode: 'none' };

export function isRailHandle(value: unknown): value is RailHandle {
  if (typeof value !== 'object' || value === null) return false;
  const rail = (value as { rail?: unknown }).rail;
  if (typeof rail !== 'object' || rail === null) return false;
  const mode = (rail as { mode?: unknown }).mode;
  return mode === 'shelf' || mode === 'siblings' || mode === 'state' || mode === 'none';
}
